import { logger } from '@vagabondinternet/logging';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authErrorCode, browserIdentitySession, type IdentitySession, verificationLinkFailure } from '../../api';
import { type BrowserNavigation, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';
import { HandoffScreen, useSessionHandoff } from '../../session';
import { useResend } from '../use-resend';
import {
  VERIFICATION_MESSAGES,
  VERIFICATION_RESEND_MESSAGES,
  verificationErrorMessage,
} from '../verify-email/verification-messages';
import { EmailVerifiedScreen, type EmailVerifiedState, type PlayerStatus } from './email-verified-screen';

/** The query parameters Identity Platform's action links carry (and ENG-852's handler passes on). */
export const ACTION_CODE_PARAM = 'oobCode';
export const ACTION_MODE_PARAM = 'mode';
export const VERIFY_EMAIL_MODE = 'verifyEmail';

const RESEND_OPTIONS = { messages: VERIFICATION_RESEND_MESSAGES };

export interface VerifyEmailConfirmPageProps {
  /** Firebase's signed-in user, applyActionCode and the API's session endpoints. Defaults to the browser's. */
  session?: IdentitySession;
  /** How a verified player leaves the app. Defaults to the window's history and location. */
  navigation?: BrowserNavigation;
}

/** The oobCode of a verify-email link, or undefined when it is missing or the link is for another action. */
export const verificationCode = (params: URLSearchParams): string | undefined => {
  const mode = params.get(ACTION_MODE_PARAM);
  const code = params.get(ACTION_CODE_PARAM);
  return code && (mode === null || mode === VERIFY_EMAIL_MODE) ? code : undefined;
};

/**
 * /verify-email/confirm?oobCode=…: applies a verification link's code (applyActionCode) and shows the outcome. A
 * verified player continues into the game through the session handoff; with no one signed in, they sign in. Until the
 * custom action handler (ENG-852) links here, verification links go through Firebase's own handler instead.
 */
export const VerifyEmailConfirmPage: React.FC<VerifyEmailConfirmPageProps> = ({
  session = browserIdentitySession,
  navigation,
}) => {
  const { switchTo, leave, returnTo, continueUrl } = useAuthNavigation(navigation);
  const [params] = useSearchParams();
  const [code] = useState(() => verificationCode(params));
  const [state, setState] = useState<EmailVerifiedState>('checking');
  const [player, setPlayer] = useState<PlayerStatus>('none');
  const [error, setError] = useState<string>();
  // React StrictMode mounts twice in development; a code is applied once per attempt.
  const started = useRef(false);

  const toVerifyEmail = useCallback(() => switchTo(ROUTES.verifyEmail), [switchTo]);
  const handoff = useSessionHandoff({ session, returnTo, leave, onUnverified: toVerifyEmail });
  const resend = useResend(
    useCallback(() => session.resendVerification(continueUrl), [session, continueUrl]),
    RESEND_OPTIONS,
  );

  /** Who is signed in on this device, reloaded so a code just applied shows. Never fails: unknown is no one. */
  const currentPlayer = useCallback(async (): Promise<PlayerStatus> => {
    if (!session.mayBeSignedIn()) {
      return 'none';
    }
    try {
      const user = await session.refreshUser();
      return user ? (user.emailVerified ? 'verified' : 'unverified') : 'none';
    } catch (cause) {
      logger.warn({ message: 'Reloading the signed-in user failed', code: authErrorCode(cause) });
      return 'none';
    }
  }, [session]);

  const apply = useCallback(async () => {
    setState('checking');
    setError(undefined);
    if (!code) {
      setPlayer(await currentPlayer());
      setState('invalid');
      return;
    }
    try {
      await session.applyVerificationCode(code);
      setPlayer(await currentPlayer());
      setState('verified');
    } catch (cause) {
      logger.error({ message: 'Applying the verification link failed', code: authErrorCode(cause) });
      const signedIn = await currentPlayer();
      setPlayer(signedIn);
      const failure = verificationLinkFailure(cause, signedIn === 'verified');
      if (failure) {
        setState(failure);
      } else {
        setError(verificationErrorMessage(cause, VERIFICATION_MESSAGES.checkFailed));
        setState('failed');
      }
    }
  }, [code, session, currentPlayer]);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    void apply();
  }, [apply]);

  const carryOn = () => {
    switch (player) {
      case 'verified':
        void handoff.complete();
        return;
      case 'unverified':
        toVerifyEmail();
        return;
      default:
        switchTo(ROUTES.signIn);
    }
  };

  return handoff.state.phase === 'idle' ? (
    <EmailVerifiedScreen
      state={state}
      player={player}
      resend={resend}
      error={error}
      onContinue={carryOn}
      onRetry={() => void apply()}
    />
  ) : (
    <HandoffScreen
      handoff={{
        ...handoff,
        // "Sign in again" signs out of Firebase, so the link's screen then offers sign-in.
        startOver: async () => {
          await handoff.startOver();
          setPlayer('none');
        },
      }}
    />
  );
};
